import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText, ListItemButton } from "@mui/material";

interface Claim {
    value: string;
    name: string;
    module?: string;
    description?: string;
}

interface AddClaimDialogProps {
    open: boolean;
    claimsToAdd: Claim[];
    selectedClaim: Claim | null;
    onSelect: (claim: Claim) => void;
    onClose: () => void;
    onAdd: () => void;
}

export const AddClaimDialog: React.FC<AddClaimDialogProps> = ({ open, claimsToAdd, selectedClaim, onSelect, onClose, onAdd }) => {
    if (claimsToAdd.length === 0) {
        return (
            <Dialog open={open} onClose={onClose}>
                <DialogTitle>Select a Claim to Add</DialogTitle>
                <DialogContent>
                    <ListItem>No available claims to add.</ListItem>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button onClick={onAdd} disabled={true} variant="contained">Add</Button>
                </DialogActions>
            </Dialog>
        );
    }

    // Group claims by module
    const claimsByModule = claimsToAdd.reduce((groups, claim) => {
        const module = claim.module || 'Other';
        if (!groups[module]) {
            groups[module] = [];
        }
        groups[module].push(claim);
        return groups;
    }, {} as Record<string, Claim[]>);

    // Sort modules alphabetically and sort claims within each module
    const sortedModules = Object.keys(claimsByModule).sort();
    sortedModules.forEach(module => {
        claimsByModule[module].sort((a, b) => a.name.localeCompare(b.name));
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Select a Claim to Add</DialogTitle>
            <DialogContent>
                <List>
                    {sortedModules.map((module) => (
                        <React.Fragment key={module}>
                            {/* Module header */}
                            <ListItem sx={{ backgroundColor: '#f8fafc', fontWeight: 'bold', py: 1 }}>
                                <ListItemText 
                                    primary={module}
                                    primaryTypographyProps={{ 
                                        variant: 'subtitle2', 
                                        fontWeight: 600,
                                        color: '#1e293b'
                                    }}
                                />
                            </ListItem>
                            
                            {/* Claims in this module */}
                            {claimsByModule[module].map((claim) => (
                                <ListItem key={claim.value} disablePadding sx={{ pl: 2 }}>
                                    <ListItemButton
                                        selected={selectedClaim?.value === claim.value}
                                        onClick={() => onSelect(claim)}
                                        sx={{
                                            '&.Mui-selected': {
                                                backgroundColor: '#e0f2fe',
                                                '&:hover': {
                                                    backgroundColor: '#b3e5fc',
                                                },
                                            },
                                        }}
                                    >
                                        <ListItemText 
                                            primary={claim.name} 
                                            secondary={
                                                <>
                                                    <span style={{ color: '#64748b' }}>Code: {claim.value}</span>
                                                    {claim.description && (
                                                        <><br /><span style={{ color: '#475569' }}>{claim.description}</span></>
                                                    )}
                                                </>
                                            }
                                            secondaryTypographyProps={{
                                                component: 'div'
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </React.Fragment>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={onAdd} disabled={!selectedClaim} variant="contained">Add</Button>
            </DialogActions>
        </Dialog>
    );
};
